export const AADHAAR_MASK_PREFIX = 'XXXX-XXXX-';

export const isAadhaarDocumentType = (documentType) =>
  String(documentType || '').trim().toLowerCase() === 'aadhaar';

/** Digits only, max 12 (full Aadhaar). */
export const getAadhaarDigits = (value) =>
  String(value || '').replace(/\D/g, '').slice(0, 12);

/** @deprecated Prefer getAadhaarDigits; kept for masked display helpers. */
export const getAadhaarLast4Digits = (value) =>
  getAadhaarDigits(value).slice(-4);

/** Format as XXXX-XXXX-XXXX for display/input (stores digits separately). */
export const formatAadhaarDisplay = (value) => {
  const digits = getAadhaarDigits(value);
  if (!digits) return '';
  const parts = [];
  if (digits.length > 0) parts.push(digits.slice(0, 4));
  if (digits.length > 4) parts.push(digits.slice(4, 8));
  if (digits.length > 8) parts.push(digits.slice(8, 12));
  return parts.join('-');
};

/**
 * Normalize typed ID for controlled inputs.
 * Aadhaar: keep up to 12 digits (formatted XXXX-XXXX-XXXX in the field).
 */
export const normalizeDocumentIdInput = (documentType, value) => {
  if (isAadhaarDocumentType(documentType)) {
    return formatAadhaarDisplay(value);
  }
  return String(value || '').toUpperCase();
};

/**
 * Value to send to API. Aadhaar: full 12 digits (or legacy last-4 until re-entered).
 */
export const normalizeDocumentIdForSubmit = (documentType, value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (isAadhaarDocumentType(documentType)) {
    return getAadhaarDigits(raw);
  }
  return raw.toUpperCase();
};

/** True when Aadhaar value is complete (12) or legacy last-4 still stored. */
export const isValidAadhaarDigits = (value) => {
  const digits = getAadhaarDigits(value);
  return digits.length === 12 || digits.length === 4;
};

/** Client-side Aadhaar validation message (empty string when valid / empty). */
export const validateAadhaarIdNumber = (value) => {
  const digits = getAadhaarDigits(value);
  if (!digits) return 'ID number is required';
  if (digits.length === 12 || digits.length === 4) return '';
  return 'Enter full 12-digit Aadhaar number';
};

export const isNoDocumentType = (documentType) => {
  const t = String(documentType || '').trim().toLowerCase();
  return !t || t === 'no_document';
};

/** True when a front/back image value is present (data URL or remote URL). */
export const hasGuestDocumentImage = (value) =>
  Boolean(value && String(value).trim());

/**
 * When a real document type is selected, at least one of front or back is required.
 * Returns { id_front, id_back } error strings (same message on both when neither uploaded).
 */
export const validateGuestDocumentImages = (documentType, idFront, idBack) => {
  if (isNoDocumentType(documentType)) {
    return { id_front: '', id_back: '' };
  }
  if (hasGuestDocumentImage(idFront) || hasGuestDocumentImage(idBack)) {
    return { id_front: '', id_back: '' };
  }
  const message = 'Document front or back image is required.';
  return { id_front: message, id_back: message };
};

/**
 * Passport: Indian (1 letter + 7 digits) or international alphanumeric 5–20.
 * Empty string when valid; message when invalid. Empty input returns required message only if needed by caller.
 */
export const isValidPassportIdNumber = (value) => {
  const normalized = String(value || '').replace(/\s+/g, '').toUpperCase();
  if (!normalized) return false;
  return /^[A-Z][0-9]{7}$/.test(normalized) || /^[A-Z0-9]{5,20}$/.test(normalized);
};

export const validatePassportIdNumber = (value) => {
  const normalized = String(value || '').replace(/\s+/g, '').toUpperCase();
  if (!normalized) return 'ID number is required';
  if (isValidPassportIdNumber(normalized)) return '';
  return 'Passport number must be 5-20 letters/numbers (e.g. A1234567)';
};

/** Walk-in / guest-details document number rules. Empty string when valid. */
export const validateDocumentIdNumber = (documentType, idNumber) => {
  const docType = String(documentType || '').trim();
  const raw = String(idNumber || '').trim();

  if (!docType && !raw) return '';
  if (!docType && raw) return 'Select document type before entering ID number';
  if (isNoDocumentType(docType)) return '';
  if (!raw) return 'ID number is required';

  const normalized = raw.replace(/\s+/g, '').toUpperCase();

  if (docType === 'aadhaar') {
    return validateAadhaarIdNumber(normalized);
  }
  if (docType === 'passport') {
    return isValidPassportIdNumber(normalized)
      ? ''
      : 'Passport number must be 5-20 letters/numbers (e.g. A1234567)';
  }
  if (docType === 'voter_id') {
    return /^[A-Z]{3}[0-9]{7}$/.test(normalized)
      ? ''
      : 'Voter ID must be 3 letters followed by 7 digits (e.g. ABC1234567)';
  }
  if (docType === 'pan') {
    return /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(normalized)
      ? ''
      : 'PAN must be 5 letters, 4 digits, and 1 letter (e.g. ABCDE1234F)';
  }
  if (docType === 'driving_license') {
    return /^[A-Z0-9-]{10,20}$/.test(normalized)
      ? ''
      : 'Driving licence number must be 10-20 characters';
  }
  return /^[A-Z0-9][A-Z0-9\-/]{3,29}$/.test(normalized)
    ? ''
    : 'ID number must be 4-30 characters (letters, numbers, -, /)';
};

export const getDocumentNumberPlaceholder = (documentType) => {
  const t = String(documentType || '').trim();
  if (t === 'aadhaar') return 'XXXX-XXXX-XXXX';
  if (t === 'passport') return 'A1234567';
  if (t === 'pan') return 'ABCDE1234F';
  if (t === 'voter_id') return 'ABC1234567';
  return 'Enter ID number';
};
